import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Send, Gift, Trash2, Users, Receipt, Mail, BarChart3 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const TABS = [
  { key: "customers", label: "Customers", icon: Users },
  { key: "transactions", label: "Transactions", icon: Receipt },
  { key: "comp", label: "Comp access", icon: Gift },
  { key: "email", label: "Email log", icon: Mail },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
];

const PART_LABEL = { part_1: "The Role", part_2: "The People", part_3: "The Moments", bundle: "All three", team_5: "Team · 5", team_10: "Team · 10", team_15: "Team · 15", team_20: "Team · 20", team_25: "Team · 25" };
const fmtDate = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "—");
const fmtET = (s) => (s ? new Date(s).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" }) + " ET" : "—");

function LastChanceCard() {
  const [st, setSt] = useState(null);
  const [sending, setSending] = useState(false);
  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/admin/campaigns/last-chance"); setSt(data); } catch { setSt(null); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const send = async () => {
    if (!window.confirm(`Send the last-chance gift email to ${st?.pending ?? 0} people now?`)) return;
    setSending(true);
    try { const { data } = await api.post("/admin/campaigns/last-chance/send"); toast.success(`Sent to ${data.sent} people`); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send"); }
    finally { setSending(false); }
  };
  if (!st) return null;
  const stats = [["Auto-send", fmtET(st.scheduled_at)], ["Offer ends", fmtET(st.offer_ends_at)], ["Sent", st.sent], ["Waiting", st.pending], ["Checkout reminders sent", st.reminders_sent], ["Unsubscribed", st.unsubscribes]];
  return (
    <div className="mb-6 rounded-2xl border border-nm-coral/25 bg-nm-surface p-6 nm-shadow-sm" data-testid="last-chance-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-nm-coral">Gift offer · last-chance email</div>
          <p className="text-sm text-nm-neutral mt-2 max-w-xl">Goes to people who left checkout and account holders who haven't bought. Nobody gets it twice, and unsubscribed people are skipped.</p>
        </div>
        <button onClick={send} disabled={sending || !st.offer_active || st.pending === 0} data-testid="last-chance-send-btn"
          className="inline-flex items-center gap-2 rounded-full bg-nm-coral px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity duration-200 disabled:opacity-40">
          <Send size={15} /> {sending ? "Sending…" : "Send now"}
        </button>
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map(([l, v]) => (
          <div key={l} data-testid={`last-chance-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-xs uppercase tracking-wide text-nm-neutral">{l}</div>
            <div className="font-serif text-xl text-nm-ink mt-1">{v}</div>
          </div>
        ))}
      </div>
      {!st.offer_active && <p className="text-xs text-nm-neutral mt-4 italic" data-testid="last-chance-inactive">The gift offer has ended, so this email is switched off.</p>}
    </div>
  );
}

function StatusPill({ s }) {
  const paid = s === "paid";
  return <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${paid ? "bg-nm-green/15 text-nm-green" : "bg-nm-ink/[0.06] text-nm-neutral"}`}>{(s || "—").toUpperCase()}</span>;
}

export default function AdminCenter() {
  const [tab, setTab] = useState("customers");
  const [counts, setCounts] = useState({});
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailFilter, setEmailFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [comp, setComp] = useState({ email: "", note: "", part: "bundle" });
  const [granting, setGranting] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const emailQs = [emailFilter ? `email=${encodeURIComponent(emailFilter)}` : "", kindFilter ? `kind=${encodeURIComponent(kindFilter)}` : ""].filter(Boolean).join("&");
      const map = { customers: "/admin/customers", transactions: "/admin/transactions", comp: "/admin/comp-access", email: `/admin/email-log${emailQs ? `?${emailQs}` : ""}`, analytics: "/admin/analytics" };
      const { data: d } = await api.get(map[t]);
      setData((prev) => ({ ...prev, [t]: d }));
      setCounts((prev) => ({
        ...prev,
        customers: t === "customers" ? d.count : prev.customers,
        transactions: t === "transactions" ? d.summary?.total : prev.transactions,
        comp: t === "comp" ? d.grants?.length : prev.comp,
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [emailFilter, kindFilter]);

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const resend = async (email) => {
    try { await api.post("/admin/resend-access", { email }); toast.success(`Sign-in link sent to ${email}`); }
    catch (e) { toast.error("Couldn't send link"); }
  };
  const grant = async () => {
    if (!comp.email) return toast.error("Enter an email");
    setGranting(true);
    try {
      await api.post("/admin/comp-access", comp);
      toast.success(`Access granted to ${comp.email}`);
      setComp({ email: "", note: "", part: "bundle" });
      load("comp");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to grant"); }
    finally { setGranting(false); }
  };
  const compResend = async (id, email) => {
    try { await api.post(`/admin/comp-access/${id}/resend`); toast.success(`Resent to ${email}`); }
    catch { toast.error("Couldn't resend"); }
  };
  const giftResend = async (sid, email) => {
    try { await api.post(`/admin/transactions/${sid}/resend-gift`); toast.success(`Gift resent to ${email}`); }
    catch { toast.error("Couldn't resend gift"); }
  };
  const compRevoke = async (id) => {
    try { await api.delete(`/admin/comp-access/${id}`); toast.success("Access revoked"); load("comp"); }
    catch { toast.error("Couldn't revoke"); }
  };

  const cur = data[tab] || {};

  return (
    <div className="max-w-none" data-testid="admin-center">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-nm-primary">Admin</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2 leading-none">Behind the kit.</h1>
        </div>
        <button onClick={() => load(tab)} data-testid="admin-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-nm-primary/25 px-5 py-2.5 text-sm font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors duration-200">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-8 inline-flex flex-wrap gap-1 rounded-2xl bg-nm-ink/[0.04] p-1.5">
        {TABS.map((t) => {
          const active = t.key === tab;
          const c = counts[t.key];
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors duration-200 ${active ? "bg-white text-nm-ink nm-shadow-sm" : "text-nm-neutral hover:text-nm-ink"}`}>
              {t.label}{c != null ? ` (${c})` : ""}
            </button>
          );
        })}
      </div>

      <div className="mt-7">
        {/* CUSTOMERS */}
        {tab === "customers" && (
          <div className="rounded-2xl border border-nm-primary/10 bg-nm-surface overflow-hidden nm-shadow-sm">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-[0.14em] text-nm-neutral bg-nm-ink/[0.03]">
                <th className="px-5 py-4 font-semibold">Email</th><th className="px-5 py-4 font-semibold">Owns</th>
                <th className="px-5 py-4 font-semibold">First purchase</th><th className="px-5 py-4 font-semibold">Last access</th>
                <th className="px-5 py-4 font-semibold text-right">Action</th></tr></thead>
              <tbody>
                {(cur.customers || []).map((c, i) => (
                  <tr key={i} className="border-t border-nm-ink/[0.06]" data-testid="customer-row">
                    <td className="px-5 py-4 text-nm-ink">{c.email}</td>
                    <td className="px-5 py-4 text-nm-neutral">{c.entitlements.bundle_owner ? "All three" : ["has_part_1", "has_part_2", "has_part_3"].filter((k) => c.entitlements[k]).map((k) => PART_LABEL[k.replace("has_", "")]).join(", ") || "—"}</td>
                    <td className="px-5 py-4 text-nm-neutral">{c.first_purchase || "—"}</td>
                    <td className="px-5 py-4 text-nm-neutral">{fmtDate(c.last_access)}</td>
                    <td className="px-5 py-4 text-right"><button onClick={() => resend(c.email)} data-testid="resend-access-btn" className="inline-flex items-center gap-1.5 text-nm-primary hover:underline"><Send size={14} /> Resend link</button></td>
                  </tr>
                ))}
                {!loading && (cur.customers || []).length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-nm-neutral">No customers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* TRANSACTIONS */}
        {tab === "transactions" && (
          <>
            {cur.summary && (
              <div className="mb-5 rounded-2xl border border-[#C2952B]/25 bg-[#C2952B]/[0.08] px-6 py-4 text-sm text-nm-ink/85" data-testid="txn-summary">
                <span className="font-medium text-nm-ink">{cur.summary.paid} paid</span> · {cur.summary.delivered} delivered · <span className="text-nm-neutral">{cur.summary.abandoned} not completed</span> · {cur.summary.total} total
              </div>
            )}
            <div className="rounded-2xl border border-nm-primary/10 bg-nm-surface overflow-hidden nm-shadow-sm">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-[0.14em] text-nm-neutral bg-nm-ink/[0.03]">
                  <th className="px-5 py-4 font-semibold">Email</th><th className="px-5 py-4 font-semibold">Amount</th><th className="px-5 py-4 font-semibold">Status</th>
                  <th className="px-5 py-4 font-semibold">Toolkit</th><th className="px-5 py-4 font-semibold">Created</th><th className="px-5 py-4 font-semibold">Fulfilled</th>
                  <th className="px-5 py-4 font-semibold">Gift</th></tr></thead>
                <tbody>
                  {(cur.transactions || []).map((t, i) => (
                    <tr key={i} className="border-t border-nm-ink/[0.06]" data-testid="txn-row">
                      <td className="px-5 py-4 text-nm-ink">{t.email}</td>
                      <td className="px-5 py-4 text-nm-neutral">${Number(t.amount || 0).toFixed(2)} {(t.currency || "usd").toUpperCase()}</td>
                      <td className="px-5 py-4"><StatusPill s={t.status} /></td>
                      <td className="px-5 py-4 text-nm-neutral">{PART_LABEL[t.lookup_key] || t.lookup_key}</td>
                      <td className="px-5 py-4 text-nm-neutral">{fmtDate(t.created_at)}</td>
                      <td className="px-5 py-4 text-nm-neutral">{t.fulfilled ? "Yes" : "No"}</td>
                      <td className="px-5 py-4" data-testid="txn-gift-cell">
                        {t.gift_to ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1 text-[13px] text-nm-ink">
                              <Gift size={13} className="text-nm-coral shrink-0" /> {t.gift_to}
                            </span>
                            <span className={`text-[11px] font-medium ${t.gift_delivered ? "text-nm-primary" : t.gift_fulfilled ? "text-nm-coral" : "text-nm-neutral"}`}>
                              {t.gift_delivered ? "Delivered" : t.gift_fulfilled ? "Email failed — resend" : "Pending"}
                            </span>
                            <button onClick={() => giftResend(t.session_id, t.gift_to)} data-testid="resend-gift-btn"
                              className="inline-flex items-center gap-1 text-[12px] text-nm-primary hover:underline w-fit"><Send size={12} /> Resend gift</button>
                          </div>
                        ) : <span className="text-nm-neutral/50">—</span>}
                      </td>
                    </tr>
                  ))}
                  {!loading && (cur.transactions || []).length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-nm-neutral">No transactions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* COMP ACCESS */}
        {tab === "comp" && (
          <>
            <div className="rounded-2xl border border-nm-primary/12 bg-nm-primary/[0.04] p-7" data-testid="comp-grant-card">
              <div className="flex items-center gap-2.5"><Gift size={20} className="text-nm-primary" /><h2 className="font-serif text-2xl text-nm-ink">Grant complimentary access</h2></div>
              <p className="text-sm text-nm-neutral mt-2 max-w-2xl">Send a toolkit to a reviewer, beta tester, or friend without a Stripe charge. They'll get a magic-link email to sign in.</p>
              <div className="mt-5 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div><label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-nm-neutral">Email</label>
                  <input value={comp.email} onChange={(e) => setComp({ ...comp, email: e.target.value })} placeholder="reviewer@example.com" data-testid="comp-email" className="mt-1.5 w-full rounded-xl border border-nm-primary/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-nm-primary" /></div>
                <div><label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-nm-neutral">Toolkit</label>
                  <select value={comp.part} onChange={(e) => setComp({ ...comp, part: e.target.value })} data-testid="comp-part" className="mt-1.5 w-full rounded-xl border border-nm-primary/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-nm-primary">
                    <option value="bundle">All three</option><option value="part_1">The Role</option><option value="part_2">The People</option><option value="part_3">The Moments</option></select></div>
                <button onClick={grant} disabled={granting} data-testid="comp-grant-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-nm-coral px-6 py-2.5 text-sm font-semibold text-white hover:brightness-105 transition-[filter] duration-200 disabled:opacity-60"><Send size={15} /> {granting ? "Granting…" : "Grant access"}</button>
              </div>
              <input value={comp.note} onChange={(e) => setComp({ ...comp, note: e.target.value })} placeholder="Note (optional) — beta tester, friend, editor…" data-testid="comp-note" className="mt-3 w-full rounded-xl border border-nm-primary/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-nm-primary" />
            </div>
            <div className="mt-6 rounded-2xl border border-nm-primary/10 bg-nm-surface overflow-hidden nm-shadow-sm">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-[0.14em] text-nm-neutral bg-nm-ink/[0.03]">
                  <th className="px-5 py-4 font-semibold">Email</th><th className="px-5 py-4 font-semibold">Toolkit</th><th className="px-5 py-4 font-semibold">Note</th>
                  <th className="px-5 py-4 font-semibold">Granted</th><th className="px-5 py-4 font-semibold text-right">Actions</th></tr></thead>
                <tbody>
                  {(cur.grants || []).map((g) => (
                    <tr key={g.id} className={`border-t border-nm-ink/[0.06] ${g.revoked ? "opacity-50" : ""}`} data-testid="comp-row">
                      <td className="px-5 py-4 text-nm-ink">{g.email}{g.revoked ? " (revoked)" : ""}</td>
                      <td className="px-5 py-4 text-nm-neutral">{PART_LABEL[g.part] || g.part}</td>
                      <td className="px-5 py-4 text-nm-neutral">{g.note || "—"}</td>
                      <td className="px-5 py-4 text-nm-neutral">{fmtDate(g.granted_at)}</td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button onClick={() => compResend(g.id, g.email)} className="inline-flex items-center gap-1.5 text-nm-primary hover:underline mr-4"><Send size={14} /> Resend</button>
                        {!g.revoked && <button onClick={() => compRevoke(g.id)} className="inline-flex items-center gap-1.5 text-nm-coral hover:underline"><Trash2 size={14} /> Revoke</button>}
                      </td>
                    </tr>
                  ))}
                  {!loading && (cur.grants || []).length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-nm-neutral">No complimentary grants yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* EMAIL LOG */}
        {tab === "email" && (
          <>
            <div className="mb-4 flex gap-3">
              <input value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} placeholder="Filter by recipient email…" data-testid="email-filter" className="flex-1 rounded-xl border border-nm-primary/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-nm-primary" />
              <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="email-kind-filter" className="rounded-xl border border-nm-primary/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-nm-primary">
                <option value="">All types</option>
                <option value="purchase">Purchase / access</option>
                <option value="alert">Purchase alerts</option>
                <option value="nurture">Nurture (2-day)</option>
                <option value="magic">Sign-in links</option>
                <option value="generic">Other</option>
              </select>
              <button onClick={() => load("email")} className="rounded-full bg-nm-primary px-6 py-2.5 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200">Search</button>
            </div>
            <div className="rounded-2xl border border-nm-primary/10 bg-nm-surface overflow-hidden nm-shadow-sm">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-[0.14em] text-nm-neutral bg-nm-ink/[0.03]">
                  <th className="px-5 py-4 font-semibold">When</th><th className="px-5 py-4 font-semibold">Recipient</th><th className="px-5 py-4 font-semibold">Subject</th><th className="px-5 py-4 font-semibold text-right">Status</th></tr></thead>
                <tbody>
                  {(cur.emails || []).map((e, i) => (
                    <tr key={i} className="border-t border-nm-ink/[0.06]" data-testid="email-row">
                      <td className="px-5 py-4 text-nm-neutral">{fmtDate(e.created_at)}</td>
                      <td className="px-5 py-4 text-nm-ink">{e.to}</td>
                      <td className="px-5 py-4 text-nm-neutral">{e.subject}</td>
                      <td className="px-5 py-4 text-right"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${e.status === "failed" ? "bg-nm-coral/15 text-nm-coral" : "bg-nm-ink/[0.06] text-nm-neutral"}`}>{(e.status || "sent").toUpperCase()}</span></td>
                    </tr>
                  ))}
                  {!loading && (cur.emails || []).length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-nm-neutral">No emails logged yet. New emails will appear here.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ANALYTICS */}
        {tab === "analytics" && <LastChanceCard />}
        {tab === "analytics" && cur && (
          <div data-testid="analytics-panel">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { label: "Landing views", v: cur.landing_views },
                { label: "Customers", v: cur.customers },
                { label: "PDF downloads", v: cur.pdf_downloads },
                { label: "Checkout starts", v: cur.checkout_starts },
                { label: "Conversion", v: `${cur.conversion ?? 0}%`, sub: "Landing → paid" },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-nm-primary/10 bg-nm-surface p-5 nm-shadow-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-nm-neutral">{s.label}</div>
                  <div className="font-serif text-4xl text-nm-ink mt-2">{s.v ?? 0}</div>
                  {s.sub && <div className="text-xs text-nm-neutral mt-1">{s.sub}</div>}
                </div>
              ))}
            </div>
            {cur.funnel && (
              <div className="mt-6 rounded-2xl border border-nm-primary/10 bg-nm-surface p-6 nm-shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-nm-neutral">Cart funnel</div>
                <div className="mt-4 grid sm:grid-cols-4 gap-4">
                  {[["Started checkout", cur.funnel.started], ["Completed", cur.funnel.completed], ["Abandoned", cur.funnel.abandoned], ["Completion rate", `${cur.funnel.rate}%`]].map(([l, v]) => (
                    <div key={l}><div className="text-xs uppercase tracking-wide text-nm-neutral">{l}</div><div className="font-serif text-2xl text-nm-ink mt-1">{v}</div></div>
                  ))}
                </div>
                <div className="mt-5 h-2.5 w-full rounded-full bg-nm-coral/25 overflow-hidden">
                  <div className="h-full bg-nm-green" style={{ width: `${cur.funnel.rate}%` }} />
                </div>
                <p className="text-[13px] text-nm-neutral mt-4 italic">Abandoned carts are buyers who clicked Buy but didn't finish payment. Normal range is 50–70% completion for digital products.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
